/**
 * PlaceholderModule — componente reutilizado pelos módulos que ainda
 * dependem de integração externa pra funcionar.
 *
 * O objetivo é o PM olhar a tela e entender: "o módulo está pronto,
 * só falta o dono externo liberar o dado". Por isso mostramos:
 *   1. O que o módulo VAI fazer (descrição funcional)
 *   2. O que está bloqueando (PendingIntegration)
 *   3. Checklist do que está feito vs bloqueado
 */

import type { ReactNode } from 'react';
import { Card, CardTitle, PendingIntegrationCard } from '@/ui';
import type { PendingIntegration } from '@/services/datasources';

interface Props {
  modulo: string;                 // 'M5'
  titulo: string;                 // 'Câmeras'
  icone?: string;
  /** O que o módulo VAI fazer quando estiver completo. */
  descricaoFuncional: string;
  /** Vem do datasource. */
  pending: PendingIntegration;
  /** O que está pronto na POC (frontend, schema, etc). */
  prontoNaPoc: string[];
  /** O que falta — depende do owner externo. */
  bloqueado: string[];
  /** Conteúdo opcional (mock UI, preview, etc) abaixo dos cards. */
  children?: ReactNode;
}

export function PlaceholderModule({
  modulo, titulo, icone, descricaoFuncional, pending, prontoNaPoc, bloqueado, children,
}: Props) {
  return (
    <div>
      <header className="mb-5">
        <div className="flex items-center gap-2 text-xs text-jf-dim uppercase tracking-wider mb-1">
          <span>Módulo</span><span>{modulo}</span>
        </div>
        <h1 className="text-2xl font-black text-jf-yellow flex items-center gap-2">
          {icone && <span aria-hidden>{icone}</span>}
          {titulo}
        </h1>
        <p className="text-jf-muted mt-1 max-w-2xl">{descricaoFuncional}</p>
      </header>

      <PendingIntegrationCard
        integration={`${pending.owner} — ${pending.name}`}
        owner={pending.owner}
        eta={pending.eta}
        description={pending.description}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <Card>
          <CardTitle>
            <span className="text-green-400">✓</span> Pronto na POC
          </CardTitle>
          <ul className="space-y-1.5 text-sm text-jf-muted">
            {prontoNaPoc.map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-green-400 mt-0.5">✓</span><span>{item}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <CardTitle>
            <span className="text-orange-400">⏳</span> Bloqueado por terceiro
          </CardTitle>
          <ul className="space-y-1.5 text-sm text-jf-muted">
            {bloqueado.map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-orange-400 mt-0.5">⏳</span><span>{item}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {children && <div className="mt-6">{children}</div>}
    </div>
  );
}
